import httpService from '@Utils/httpService';

import { buildRegisterImportRequest } from '../modules/registerImportContracts';

const baseUrl = 'api/registers';

export default {
  /**
   * Get all registers with optional filtering
   * @param {string} facilityId - Optional facility ID filter
   * @param {string} userId - Optional user ID filter for assigned registers
   * @returns {Promise} Array of registers
   */
  async getRegisters(facilityId, userId) {
    // -1 = every register. Without it the API's ToPage() falls back to a 20-row page, which
    // silently truncated Register Management (client-side pagination, owner and mode filters).
    const params = { itemsPerPage: -1 };
    if (facilityId) params.facilityId = facilityId;
    if (userId) params.userId = userId;

    const response = await httpService.get({
      url: baseUrl,
      params
    });
    return response.data;
  },

  /**
   * Get registers for a specific user within a facility (UI-specific)
   * Backend endpoint: [HttpGet("user-facility")]
   * @param {string} facilityId
   */
  async getUserFacilityRegisters(facilityId) {
    const params = { facilityId };
    const response = await httpService.get({
      url: `${baseUrl}/user-facility`,
      params
    });
    return response.data;
  },

  /**
   * Get a specific register by ID
   * @param {string} registerId - Register ID
   * @returns {Promise} Register object
   */
  async getRegister(registerId) {
    const response = await httpService.get({
      url: `${baseUrl}/${registerId}`
    });
    return response.data;
  },

  /**
   * Create a new register
   * @param {Object} registerData - Register data
   * @returns {Promise} Created register
   */
  async createRegister(registerData) {
    const response = await httpService.post({
      url: baseUrl,
      data: registerData
    });
    return response.data;
  },

  /**
   * Update an existing register
   * @param {string} id - Register ID
   * @param {Object} data - Updated register data
   * @returns {Promise} Updated register
   */
  async updateRegister(id, data) {
    const response = await httpService.put({
      url: `${baseUrl}/${id}`,
      data
    });
    return response.data;
  },

  /**
   * Delete a register
   * @param {string} registerId - Register ID
   * @returns {Promise} Deletion result
   */
  async deleteRegister(registerId) {
    const response = await httpService.delete({
      url: `${baseUrl}/${registerId}`
    });
    return response.data;
  },

  // Template Management
  /**
   * Get register data templates with optional search and pagination
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search templates by name
   * @param {number} params.page - Page number for pagination
   * @param {number} params.itemsPerPage - Number of items per page
   * @param {string} params.sortBy - Sort field (name, createdate)
   * @param {boolean} params.sortDesc - Sort descending
   * @returns {Promise} Array of RegisterDataTemplateVM objects
   */
  async getDataTemplates(params = {}) {
    const response = await httpService.get({
      url: 'api/register-data-templates',
      params
    });
    return response.data;
  },

  /**
   * Get a specific register data template by ID
   * @param {string} templateId - Template ID
   * @returns {Promise} RegisterDataTemplateVM object
   */
  async getDataTemplate(templateId) {
    const response = await httpService.get({
      url: `api/register-data-templates/${templateId}`
    });
    return response.data;
  },

  /**
   * Create a new register data template
   * @param {Object} templateData - CreateRegisterDataTemplateCommand
   * @param {string} templateData.name - Template name (required, max 255 characters)
   * @param {Object} templateData.template - RegisterFieldMapping object
   * @returns {Promise} Created RegisterDataTemplateVM
   */
  async createDataTemplate(templateData) {
    const response = await httpService.post({
      url: 'api/register-data-templates',
      data: templateData
    });
    return response.data;
  },

  /**
   * Update an existing register data template
   * @param {string} templateId - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise} Updated RegisterDataTemplateVM
   */
  async updateDataTemplate(templateId, templateData) {
    const response = await httpService.put({
      url: `api/register-data-templates/${templateId}`,
      data: templateData
    });
    return response.data;
  },

  /**
   * Delete a register data template
   * @param {string} templateId - Template ID
   * @returns {Promise} Deletion result
   */
  async deleteDataTemplate(templateId) {
    const response = await httpService.delete({
      url: `api/register-data-templates/${templateId}`
    });
    return response.data;
  },

  /**
   * Get field mapping for a specific template (for import)
   * @param {string} templateId - Template ID
   * @returns {Promise} RegisterFieldMapping object
   */
  async getTemplateFieldMapping(templateId) {
    const response = await httpService.get({
      url: `api/register-data-templates/${templateId}/field-mapping`
    });
    return response.data;
  },

  /**
   * Import validated data into register
   * @param {Object} importData - Data to import
   * @param {string} importData.registerId - Register ID
   * @param {Array} importData.data - Array of data rows
   * @param {Object} importData.mapping - Field mapping configuration including fieldTypes
   * @param {string} [importData.fileName] - Original filename (for audit logging)
   * @returns {Promise} Import results
   */
  async importData(importData) {
    // Ensure the mapping includes field types for backend processing
    const requestData = buildRegisterImportRequest(importData);

    const response = await httpService.post({
      url: `api/register-data/import`,
      data: requestData
    });
    return response.data;
  },

  /**
   * Export register data (legacy CSV-only endpoint)
   * @param {string} registerId - Register ID
   * @returns {Promise} Export file blob
   */
  async exportRegister(registerId) {
    const response = await httpService.get({
      url: `api/register-data/${registerId}/export`,
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Advanced export with format selection (CSV/XLSX), row selection, and field mapping
   * @param {string} registerId - Register ID
   * @param {Object} options - Export options
   * @param {number} options.format - ExportFormat enum (1=CSV, 2=XLSX)
   * @param {number} options.exportMode - ExportMode enum (1=Filtered, 2=Selected)
   * @param {Array<string>} options.selectedRowIds - Row IDs to export (for Selected mode)
   * @param {Object} options.columnFilters - Column filters (for Filtered mode, empty = all rows)
   * @param {Object} options.fieldMapping - Field mapping configuration
   * @param {string} options.sortBy - Column to sort by
   * @param {boolean} options.sortDesc - Sort descending
   * @param {string} [options.viewContext] - 'central' | 'member' (the API ignores the Facility filter for member;
   *   a body without it is read as central)
   * @param {string} [options.facilityId] - The member view's Facility (required by the API for viewContext 'member')
   * @param {string} [options.facilityIds] - Central Separated Facility filter, comma-separated (Filtered mode)
   * @param {boolean} [options.includeUnassigned] - Central Separated Facility filter: rows without a Facility
   * @returns {Promise} Export file blob with response object
   */
  async exportRegisterAdvanced(registerId, options = {}) {
    const response = await httpService.post({
      url: `api/register-data/${registerId}/export-advanced`,
      data: {
        format: options.format ?? 1, // Default CSV
        exportMode: options.exportMode ?? 1, // Default Filtered
        selectedRowIds: options.selectedRowIds || [],
        columnFilters: options.columnFilters || {},
        fieldMapping: options.fieldMapping || null,
        sortBy: options.sortBy,
        sortDesc: options.sortDesc,
        ...(options.viewContext ? { viewContext: options.viewContext } : {}),
        ...(options.facilityId ? { facilityId: options.facilityId } : {}),
        ...(options.facilityIds ? { facilityIds: options.facilityIds } : {}),
        ...(typeof options.includeUnassigned === 'boolean' ? { includeUnassigned: options.includeUnassigned } : {})
      },
      responseType: 'blob'
    });
    return response;
  },

  /**
   * Download blob as file - triggers browser download
   * @param {Blob} blob - File blob to download
   * @param {string} filename - Filename for download
   */
  downloadBlobAsFile(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Get filename from Content-Disposition header or use default
   * @param {Object} response - Axios response object
   * @param {string} defaultName - Default filename if header not found
   * @returns {string} Filename
   */
  getFilenameFromResponse(response, defaultName) {
    const disposition = response.headers['content-disposition'];
    if (disposition) {
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) return match[1];
    }
    return defaultName;
  },

  // Helper methods for fetching related data
  /**
   * Get available facilities
   * @returns {Promise} Array of facilities
   */
  async getFacilities() {
    const response = await httpService.get({
      url: 'api/facilities'
    });
    return response.data;
  },

  /**
   * Get available users
   * @returns {Promise} Array of users
   */
  async getUsers() {
    const response = await httpService.get({
      url: 'api/users'
    });
    return response.data;
  },

  /**
   * Get register data entries
   * @param {string} registerId - Register ID
   * @returns {Promise} Array of register entries
   */
  async getRegisterData(registerId) {
    const response = await httpService.get({
      url: `${baseUrl}/${registerId}/data`
    });
    return response.data;
  },

  /**
   * Clear all register data (hard delete)
   * @param {string} registerId - Register ID
   * @returns {Promise} Clear result
   */
  async clearRegisterData(registerId) {
    const response = await httpService.delete({
      url: `api/register-data/${registerId}/clear-all`
    });
    return response.data;
  },

  /**
   * Get register data in table format (optimized for table display)
   * @param {string} registerId - Register ID
   * @param {Object} params - Query parameters
   * @returns {Promise} Table data with columns and rows
   */
  async getRegisterTableData(registerId, params = {}, signal = null) {
    const response = await httpService.get({
      url: `api/register-data/${registerId}/table-data`,
      params,
      signal
    });
    return response.data;
  },

  /**
   * Every register row linked to one tag, grouped by register (AVA-493) — the reverse of the
   * row → tags direction the register grid already shows. Reads both link systems.
   *
   * Visibility follows the admitted view: Shared Group registers include all linked rows;
   * Separated Group registers include the selected member's rows. Results remain fail-closed
   * and capped, not paged (`truncated` says the tag has more linked rows than `rowLimit`).
   * @param {string} tagId - Tag ID
   * @param {Object} [options] - Explicit viewContext and member facilityId; omitted preserves
   * the existing Facility-tag contract. A central selection must be explicit and have no member.
   * @returns {Promise} LinkedRegisterRowsVM ({ tagId, tagName, facilityId, registers, totalRows, registerCount, truncated, rowLimit })
   */
  async getTagLinkedRegisterRows(tagId, options = {}) {
    if (options.facilityId != null && typeof options.facilityId !== 'string') {
      throw new Error('Select a valid Facility to view linked records.');
    }
    const facilityId = options.facilityId?.trim();
    const selectedContext = options.viewContext;
    if (selectedContext != null && !['member', 'central'].includes(selectedContext)) {
      throw new Error('Select a valid linked-records view.');
    }
    const viewContext = selectedContext || (facilityId ? 'member' : null);
    if (viewContext === 'member' && !facilityId) {
      throw new Error('Select a Facility to view linked records.');
    }
    if (viewContext === 'central' && facilityId) {
      throw new Error('A central linked-records view cannot include a member Facility.');
    }
    const response = await httpService.get({
      url: `api/register-data/tags/${tagId}/rows`,
      ...(viewContext ? { params: { viewContext } } : {}),
      ...(facilityId ? { headers: { 'X-Facility-Id': facilityId } } : {}),
    });
    return response.data;
  },

  /**
   * Get a register's schema (column definitions incl. link declarations) by id.
   * Wraps the existing GET api/register-data/{registerId}/schema (RegisterSchemaVM). For a
   * stored-schema register this carries linkEntityType / linkKeyField / isDefaultHighlight /
   * isDefaultContext per column — the register-link designation the dashboard resolves against.
   * @param {string} registerId - Register ID
   * @returns {Promise} RegisterSchemaVM ({ registerId, columns, totalRows, ... })
   */
  async getRegisterSchema(registerId) {
    const response = await httpService.get({
      url: `api/register-data/${registerId}/schema`
    });
    return response.data;
  },

  /**
   * Update register row data
   * @param {string} rowId - Row ID
   * @param {Object} data - Dynamic data fields to update
   * @returns {Promise} Updated row data
   */
  async updateRegisterRow(rowId, data) {
    const response = await httpService.put({
      url: `api/register-data/rows/${rowId}`,
      data: { Data: data }
    });
    return response.data;
  },

  /**
   * Apply one shared field patch to many rows of a register in a single request.
   * The server treats it as all-or-nothing: a rejected row leaves every row untouched.
   * @param {string} registerId - Register ID that owns every listed row
   * @param {string[]} rowIds - Rows to update
   * @param {Object} changes - Field patch applied to every listed row
   * @returns {Promise} { success, updatedCount, errors: [{ rowId, reason }] }
   */
  async bulkUpdateRegisterRows(registerId, rowIds, changes) {
    const response = await httpService.put({
      url: `api/register-data/${registerId}/rows/bulk-update`,
      data: { RowIds: rowIds, Changes: changes }
    });
    return response.data;
  },

  /**
   * Get column filter values for select filters. The API reads them through the table-data
   * admission, so the caller passes the grid's read scope: a member view of a Separated register
   * without `viewContext` / `facilityId` is read as central and refused for member-only users.
   * @param {string} registerId - Register ID
   * @param {string} columnName - Column name to get values for
   * @param {{ viewContext?: string, facilityId?: string }} [readScope] - `registerReadScopeFields`
   *   of the grid's read context
   * @returns {Promise} Array of unique column values
   */
  async getColumnFilterValues(registerId, columnName, readScope = {}) {
    const params = { ...readScope, columnName };
    const response = await httpService.get({
      url: `api/register-data/${registerId}/column-values`,
      params
    });
    return response.data;
  },

  /**
   * Update register schema
   * @param {string} registerId - Register ID
   * @param {Object} schema - Schema configuration
   * @param {Object} schema.fieldMappings - Field name mappings
   * @param {Object} schema.fieldTypes - Field type mappings
   * @param {Object} schema.mandatoryFields - Mandatory field mappings
   * @param {string} schema.tagColumn - Tag column name
   * @param {string} schema.facilityColumn - Facility column name
   * @param {string} schema.identityColumn - Identity column name
   * @returns {Promise} Updated register
   */
  async updateRegisterSchema(registerId, schema) {
    const response = await httpService.put({
      url: `${baseUrl}/${registerId}/schema`,
      data: schema
    });
    return response.data;
  },

  // ==========================================
  // AUDIT LOG METHODS
  // ==========================================

  /**
   * Get audit logs for a register (register-wide view)
   * @param {string} registerId - Register ID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.itemsPerPage - Items per page (default: 20)
   * @param {string} params.actionType - Filter by action type
   * @param {string} params.actionCategory - Filter by action category
   * @param {string} params.userId - Filter by user ID
   * @param {string} params.fromDate - Filter from date (ISO string)
   * @param {string} params.toDate - Filter to date (ISO string)
   * @param {string} params.sortBy - Sort field (default: ActionTimestamp)
   * @param {boolean} params.sortDesc - Sort descending (default: true)
   * @returns {Promise} Paginated list of audit logs
   */
  async getRegisterAuditLogs(registerId, params = {}) {
    const response = await httpService.get({
      url: `api/register-audit-logs/register/${registerId}`,
      params
    });
    return response.data;
  },

  /**
   * Get audit logs for a specific row
   * @param {string} rowId - Row ID
   * @param {Object} params - Query parameters (same as getRegisterAuditLogs)
   * @returns {Promise} Paginated list of audit logs for the row
   */
  async getRowAuditLogs(rowId, params = {}) {
    const response = await httpService.get({
      url: `api/register-audit-logs/row/${rowId}`,
      params
    });
    return response.data;
  },

  /**
   * Get detailed audit log entry with all field changes
   * @param {string} logId - Audit log ID
   * @returns {Promise} Detailed audit log with changes array
   */
  async getAuditLogDetail(logId) {
    const response = await httpService.get({
      url: `api/register-audit-logs/${logId}`
    });
    return response.data;
  },

  // ==========================================
  // SORT INDEX METHODS
  // ==========================================

  /**
   * Enable sort index on a register column (creates a database index for faster sorting)
   * @param {string} registerId - Register ID
   * @param {string} columnName - Column name to index
   * @returns {Promise} Updated column definition with isSortIndexed = true
   */
  async enableColumnSortIndex(registerId, columnName) {
    const response = await httpService.put({
      url: `${baseUrl}/${registerId}/columns/${encodeURIComponent(columnName)}/sort-index`
    });
    return response.data;
  },

  /**
   * Disable sort index on a register column (drops the database index)
   * @param {string} registerId - Register ID
   * @param {string} columnName - Column name to remove index from
   * @returns {Promise} Updated column definition with isSortIndexed = false
   */
  async disableColumnSortIndex(registerId, columnName) {
    const response = await httpService.delete({
      url: `${baseUrl}/${registerId}/columns/${encodeURIComponent(columnName)}/sort-index`
    });
    return response.data;
  },

  // ==========================================
  // COLUMN LINK DESIGNATION
  // ==========================================

  /**
   * Declare (or clear) the entity link a single register column carries. The declaration is stored
   * inside the register's SchemaJson (no DDL), and is the input the dashboard POI resolvers read.
   *
   * COLUMN IDENTITY: `columnName` is the column's INTERNAL name (RegisterColumnDefinition.Name,
   * e.g. `field_1`) — the server matches on it (`UpdateColumnLinkAsync` → `c.Name == columnName`).
   * This is NOT the displayName; the displayName is the physical SQL-view column the dashboard
   * query selects, which is a different code path.
   *
   * Sending a null linkEntityType clears the declaration (the flags must then be false).
   * @param {string} registerId - Register ID
   * @param {string} columnName - Internal column name (RegisterColumnDefinition.Name)
   * @param {Object} link - Link declaration
   * @param {?string} link.linkEntityType - One of the RegisterLinkEntityTypes values, or null to clear
   * @param {?string} link.linkKeyField - Field of the target entity to match on (null = per-type default)
   * @param {boolean} link.isDefaultHighlight - Make this the register's default highlight link
   * @param {boolean} link.isDefaultContext - Make this the register's default context link
   * @returns {Promise} The updated register (RegisterVM)
   */
  async updateColumnLink(registerId, columnName, link = {}) {
    const response = await httpService.put({
      url: `${baseUrl}/${registerId}/columns/${encodeURIComponent(columnName)}/link`,
      data: {
        linkEntityType: link.linkEntityType || null,
        linkKeyField: link.linkKeyField || null,
        isDefaultHighlight: !!link.isDefaultHighlight,
        isDefaultContext: !!link.isDefaultContext
      }
    });
    return response.data;
  },

  /**
   * Re-resolve every row of a register into stored point-of-interest links.
   *
   * Needed whenever the designations changed AFTER the rows were imported. That is the normal
   * case for a FIRST import: the server assigns the internal column names while the import runs
   * (and runs its own resolver before it does), so the designations can only be written once the
   * import has returned — by which time the import's own resolution pass has already happened.
   * @param {string} registerId - Register ID
   * @returns {Promise} Resolution summary ({ resolved, unresolved, ambiguous, ... })
   */
  async resolveLinks(registerId) {
    const response = await httpService.post({
      url: `${baseUrl}/${registerId}/resolve-links`
    });
    return response.data;
  },

  // ==========================================
  // AUDIT LOG METHODS
  // ==========================================

  /**
   * Clear audit logs for a register (requires Registers.Delete permission)
   * @param {string} registerId - Register ID
   * @param {Date} olderThan - Optional: only clear logs older than this date
   * @returns {Promise} { success: boolean, deletedCount: number }
   */
  async clearAuditLogs(registerId, olderThan = null) {
    const params = {};
    if (olderThan) {
      params.olderThan = olderThan.toISOString();
    }
    const response = await httpService.delete({
      url: `api/register-audit-logs/register/${registerId}/clear`,
      params
    });
    return response.data;
  }
};
